import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";

import {
  getGetCaseSLAConfigurationsQueryKey,
  getGetCategoriesQueryKey,
  useCreateCategory,
} from "@vega-api/generated/case-admin-v1/case-admin-v1";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { TextareaField } from "@vega-core/components/Form/Fields/TextareaField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";

import { useMutationErrorToast } from "../../hooks/useMutationErrorToast";
import { CategoryDescriptionHelp } from "../CategoriesSettingsCard/CategoryDescriptionHelp";

import { getNewCategoryValidationSchema } from "./validationSchema";

type FormValues = {
  name: string;
  description: string;
  slaHours: string;
};

const initialValues: FormValues = {
  name: "",
  description: "",
  slaHours: "",
};

export const NewCategoryForm = () => {
  const showToast = useToast();
  const errorToast = useMutationErrorToast();
  const queryClient = useQueryClient();
  const {
    value: isModalOpen,
    setTrue: openModal,
    setFalse: closeModal,
  } = useBoolean();

  const { mutate: createCategory, isPending } = useCreateCategory({
    mutation: {
      onError: errorToast(m.cases_settings_error_createCategory()),
    },
  });

  const handleSubmit = (
    values: FormValues,
    { resetForm }: { resetForm: () => void },
  ) => {
    const slaHours = Number(values.slaHours);

    createCategory(
      {
        data: {
          name: values.name.trim(),
          description: values.description.trim() || undefined,
          slaInHours: slaHours,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetCategoriesQueryKey(),
          });
          queryClient.invalidateQueries({
            queryKey: getGetCaseSLAConfigurationsQueryKey(),
          });
          showToast({
            title: m.cases_settings_success_createCategory(),
            variant: "success",
          });
          resetForm();
          closeModal();
        },
      },
    );
  };

  return (
    <>
      <ThemedButton
        dataTestId="new-category-button"
        onClick={openModal}
        variant="primary"
      >
        {m.cases_settings_new_category()}
      </ThemedButton>

      {isModalOpen ? (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={getNewCategoryValidationSchema()}
        >
          {({ handleSubmit: submitForm, resetForm }) => (
            <ActionaryModal
              ModalButtonsGroupProps={{
                mainAction: submitForm,
                mainActionText: m.cases_settings_create_category(),
                mainActionLabel: m.cases_settings_create_category(),
                disabled: isPending,
                isLoading: isPending,
                secondaryAction: () => {
                  resetForm();
                  closeModal();
                },
              }}
              dataTestId="new-category-modal"
              open
              setOpen={(value) => {
                if (!value) {
                  resetForm();
                  closeModal();
                }
              }}
              title={m.cases_settings_new_category()}
            >
              <Form
                className="space-y-4 w-full"
                data-testid="new-category-form"
              >
                <InputField
                  label={m.cases_settings_category_name()}
                  name="name"
                  type="text"
                  withBorder
                />

                <TextareaField
                  isOptional
                  label={m.cases_settings_category_description()}
                  labelTag={<CategoryDescriptionHelp />}
                  name="description"
                />

                <InputField
                  label={m.cases_settings_category_sla()}
                  name="slaHours"
                  type="number"
                  withBorder
                />
              </Form>
            </ActionaryModal>
          )}
        </Formik>
      ) : null}
    </>
  );
};
